import React, { useEffect, useState } from "react"
import { ArrowUpFromLine } from "lucide-mui"
import i18n from "@/i18n"
import type { ShareCapableNavigator } from "./types"

const STYLES = {
  button:
    "inline-flex h-[30px] shrink-0 items-center gap-[7px] rounded-[5px] border border-gray-200 bg-white px-[10px] py-[5px] text-[12px] leading-4 text-neutral-600 transition-colors hover:bg-neutral-50",
  icon: "h-[10px] w-[10px] shrink-0",
} as const

type ShareButtonProps = {
  venueName: string
}

const ShareButton: React.FC<ShareButtonProps> = ({ venueName }) => {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timeoutId = window.setTimeout(() => setCopied(false), 2000)
    return () => window.clearTimeout(timeoutId)
  }, [copied])

  const handleShare = async () => {
    const shareUrl = window.location.href
    const browserNavigator = window.navigator as ShareCapableNavigator

    if (!shareUrl) return

    const copyShareUrl = async () => {
      if (!browserNavigator.clipboard?.writeText) return
      await browserNavigator.clipboard.writeText(shareUrl)
      setCopied(true)
    }

    if (browserNavigator.share) {
      try {
        await browserNavigator.share({ title: venueName, url: shareUrl })
        return
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") return
      }
    }

    try {
      await copyShareUrl()
    } catch {
      setCopied(false)
    }

    if (!browserNavigator.share && !browserNavigator.clipboard?.writeText) {
      setCopied(false)
    }
  }

  return (
    <button
      type="button"
      onClick={() => {
        void handleShare()
      }}
      className={STYLES.button}
      data-testid="public-venue-header-share-button"
    >
      <ArrowUpFromLine aria-hidden="true" className={STYLES.icon} strokeWidth={1.75} />
      <span aria-live="polite">
        {copied ? i18n.t("app.public.venue.share_copied") : i18n.t("app.public.venue.share")}
      </span>
    </button>
  )
}

export default ShareButton
